import {
  BACKTEST_DEFAULT_DAYS,
  BACKTEST_MAX_DAYS,
  type BacktestResponse,
  type RuleCondition,
} from '@vision360/contracts';
import { FlaskConical } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { SelectField } from '@/widgets/_sdk/fields';
import { absoluteTime } from '@/widgets/_sdk/format';
import { useBacktest } from './api';

/** "Would this have fired last week?" (PRD VS-060) — a saved rule or an unsaved condition. */

const WINDOWS = [1, BACKTEST_DEFAULT_DAYS, BACKTEST_MAX_DAYS] as const;
const DAY_MS = 86_400_000;

export function BacktestRunner({
  ruleId,
  condition,
}: {
  ruleId?: string | undefined;
  /** For an unsaved rule: the condition to test, or null when it is not valid yet. */
  condition?: (() => RuleCondition | null) | undefined;
}) {
  const { t } = useTranslation('rules');
  const [days, setDays] = useState<number>(BACKTEST_DEFAULT_DAYS);
  const [invalid, setInvalid] = useState(false);
  const backtest = useBacktest(ruleId);

  const run = () => {
    const tested = condition?.() ?? null;
    setInvalid(Boolean(condition) && !tested);
    if (condition && !tested) return;
    const to = Date.now();
    backtest.mutate({
      ...(tested ? { condition: tested } : {}),
      from: new Date(to - days * DAY_MS).toISOString(),
      to: new Date(to).toISOString(),
    });
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-36">
          <SelectField
            label={t('backtest.window')}
            value={String(days)}
            options={WINDOWS.map((d) => ({
              value: String(d),
              label: t('backtest.days', { count: d }),
            }))}
            onChange={(value) => setDays(Number(value))}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          className="h-11"
          disabled={backtest.isPending}
          onClick={run}
        >
          {backtest.isPending ? (
            <Spinner />
          ) : (
            <FlaskConical aria-hidden="true" className="h-4 w-4" />
          )}
          {backtest.isPending ? t('backtest.running') : t('backtest.run', { count: days })}
        </Button>
      </div>
      <div aria-live="polite" className="text-sm">
        {invalid && <p className="text-destructive">{t('backtest.invalid')}</p>}
        {backtest.isError && <p className="text-destructive">{t('backtest.failed')}</p>}
        {backtest.data && !invalid && <BacktestResult result={backtest.data} />}
      </div>
    </div>
  );
}

function BacktestResult({ result }: { result: BacktestResponse }) {
  const { t } = useTranslation('rules');
  if (!result.supported) {
    return <p className="animate-enter text-muted-foreground">{t('backtest.unsupported')}</p>;
  }
  return (
    <div className="animate-enter space-y-2">
      <p className="font-medium tabular-nums">{t('backtest.fires', { count: result.fires })}</p>
      {result.samples.length > 0 && (
        <>
          <h3 className="text-xs font-semibold text-muted-foreground">{t('backtest.samples')}</h3>
          <ul className="max-h-48 divide-y overflow-y-auto">
            {result.samples.map((s) => (
              <li key={`${s.at}|${s.title}`} className="flex gap-3 py-1">
                <time dateTime={s.at} className="shrink-0 tabular-nums text-muted-foreground">
                  {absoluteTime.format(new Date(s.at))}
                </time>
                <span className="min-w-0 truncate" title={s.title}>
                  {s.title}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
